/**
 * La marca: el simbolo y el bloque de cabecera.
 *
 * ===========================================================================
 * ES UN LOGO PROVISIONAL DE TRABAJO
 *
 * No lo hizo un estudio de identidad y no pretende serlo. Es lo que hace falta
 * para que la pantalla, el login, la pagina publica, la landing y la pestaña
 * del navegador dejen de ir sin marca, escrito de forma que sustituirlo cueste
 * un archivo y no una refactorizacion. **Cuando llegue la identidad
 * profesional de AGS, se reemplaza el cuerpo de `simboloMarca()` y los cuatro
 * SVG de `frontend/estatico/`, y no se toca nada mas.** El hueco, su tamaño,
 * su fondo y su radio ya estan reservados en `.logo` [estilo/base.css], asi que
 * ninguna cabecera se recoloca al cambiarlo.
 *
 * ===========================================================================
 * QUE DIBUJA, Y POR QUE ESO
 *
 * Un arco entre dos puntos: los dos puertos y el trayecto entre ellos.
 *
 * No es una figura elegida por bonita, es LA MISMA que ya dibuja el producto.
 * `vista/mapa.ts` pinta cada ruta como un arco entre el POL y el POD, con un
 * punto relleno en cada extremo; el simbolo es esa figura sin el mapa debajo.
 * Un logo que repite la forma que el usuario ve en cada busqueda dice de que
 * va esto sin explicarlo, y en un cotizador de carga internacional el arco es
 * ademas la unica figura que significa las dos cosas del nombre a la vez: el
 * puente y la travesia.
 *
 * La geometria es deliberadamente simple --dos circulos y una cubica
 * simetrica-- porque el tamaño mas usado es 16 px en una pestaña. Todo lo que
 * no sobreviva ahi es adorno.
 *
 * ===========================================================================
 * SVG EN LINEA, NO UN <img>
 *
 * Hereda `currentColor`, o sea que sigue al token del sitio donde se ponga sin
 * exportar una version por cada fondo; y no es una peticion mas ni un archivo
 * que pueda faltar en la imagen del contenedor. Los SVG de
 * `frontend/estatico/` existen para lo de fuera --favicon, landing, documentos-- y
 * llevan la misma geometria con el color de la paleta escrito.
 *
 * `document.createElementNS` y no `createElement`: un SVG creado en el espacio
 * de nombres de HTML se agrega al arbol pero no se pinta, y falla en silencio.
 * Por eso `dom.ts` no sirve aqui y esta funcion construye sus nodos a mano.
 */

import { el } from '../dom';
import type { Traductor } from '../i18n';

const SVG = 'http://www.w3.org/2000/svg';

/**
 * La geometria, en un lienzo de 48x48. Es la unica copia dentro del bundle;
 * los archivos de `frontend/estatico/` repiten estos mismos numeros porque un
 * `.svg` no puede importar.
 *
 * El arco arranca y termina EN el centro de cada punto: asi el trazo nace de
 * dentro del circulo y no se ve una junta cuando el color del arco y el del
 * punto son el mismo.
 */
export const MARCA = {
  arco: 'M9 32 C9 13 39 13 39 32',
  puertos: [
    { cx: 9, cy: 32 },
    { cx: 39, cy: 32 },
  ],
  radioPuerto: 4.2,
  grosor: 3.4,
} as const;

/**
 * El simbolo, decorativo.
 *
 * `aria-hidden` porque el nombre de la herramienta va al lado en texto, y un
 * lector de pantalla que lea las dos cosas dice el nombre dos veces.
 */
export function simboloMarca(): SVGSVGElement {
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('viewBox', '0 0 48 48');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const arco = document.createElementNS(SVG, 'path');
  arco.setAttribute('d', MARCA.arco);
  arco.setAttribute('stroke', 'currentColor');
  arco.setAttribute('stroke-width', String(MARCA.grosor));
  arco.setAttribute('stroke-linecap', 'round');
  svg.appendChild(arco);

  for (const p of MARCA.puertos) {
    const punto = document.createElementNS(SVG, 'circle');
    punto.setAttribute('cx', String(p.cx));
    punto.setAttribute('cy', String(p.cy));
    punto.setAttribute('r', String(MARCA.radioPuerto));
    punto.setAttribute('fill', 'currentColor');
    svg.appendChild(punto);
  }

  return svg;
}

/** El bloque de marca completo: simbolo, familia y nombre del modulo. */
export function pintarMarca(t: Traductor): HTMLElement[] {
  const hueco = el('div', { clase: 'logo' });
  hueco.appendChild(simboloMarca());

  return [
    hueco,
    el('div', { clase: 'marca' }, [
      // De que familia es esto. Va encima y en pequeño: el nombre del modulo es
      // lo que el agente busca, la suite es contexto.
      el('p', { clase: 'marca-suite', texto: t('app.suite') }),
      el('h1', { texto: t('app.nombre') }),
    ]),
  ];
}
